import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { firmsApi, type Account, type AccountInput, type Firm } from "../lib/api";

/**
 * Reused for create and edit. `initial`, when provided, seeds the fields.
 *
 * The `prop_firm` field is a <select> sourced from the firms table. Only
 * active firms appear by default; if we're editing an account whose firm
 * has since been deactivated, we still surface that firm in the list so the
 * value isn't silently changed (with a "(inactive)" suffix).
 *
 * `strategies_enabled` stays as a comma-separated text field for now — a
 * strategies registry is its own future feature.
 */
export default function AccountForm({
  title,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Account;
  onSubmit: (input: AccountInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [propFirm, setPropFirm] = useState(initial?.prop_firm ?? "");
  const [size, setSize] = useState<string>(initial ? String(initial.size) : "");
  const [accountNumber, setAccountNumber] = useState(initial?.account_number ?? "");
  const [active, setActive] = useState<boolean>(initial?.active ?? true);
  const [strategiesText, setStrategiesText] = useState<string>(
    (initial?.strategies_enabled ?? []).join(", "),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Firms drive the prop_firm dropdown. We refetch on tab focus and on
  // dropdown open so flips done on /firms in another tab show up without
  // making the user close and reopen this form.
  const [firms, setFirms] = useState<Firm[] | null>(null);
  const [firmsErr, setFirmsErr] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshFirms = useCallback(async () => {
    try {
      // Include inactive so an edited account's deactivated firm is still selectable.
      const items = await firmsApi.list({ includeInactive: true });
      if (!mountedRef.current) return;
      setFirms(items);
      setFirmsErr(null);
    } catch (e: unknown) {
      if (!mountedRef.current) return;
      setFirmsErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refreshFirms();
    const onFocus = () => void refreshFirms();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshFirms]);

  const firmOptions = useMemo(() => {
    if (!firms) return [];
    const active = firms.filter((f) => f.active);
    const sorted = [...active].sort((a, b) => a.name.localeCompare(b.name));
    // Edge case: editing an account whose prop_firm was deactivated. Surface
    // it in the dropdown so the form doesn't silently change the value.
    if (initial && !active.some((f) => f.name === initial.prop_firm)) {
      const inactive = firms.find((f) => f.name === initial.prop_firm);
      if (inactive) sorted.unshift(inactive);
    }
    return sorted;
  }, [firms, initial]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!propFirm) {
      setError("Please pick a prop firm.");
      return;
    }

    const strategies = strategiesText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const strategyPattern = /^CCC-[A-Z0-9]+-[A-Z0-9_]+-v[0-9]+\.[0-9]+$/;
    const bad = strategies.filter((s) => !strategyPattern.test(s));
    if (bad.length) {
      setError(
        `Invalid strategy id(s): ${bad.join(", ")}. Expected format: CCC-<INSTRUMENT>-<STRATEGY>-v<MAJOR>.<MINOR>`,
      );
      return;
    }

    const sizeNum = Number(size);
    if (!Number.isFinite(sizeNum) || sizeNum < 0) {
      setError("Size must be a non-negative number.");
      return;
    }

    const input: AccountInput = {
      name: name.trim(),
      prop_firm: propFirm.trim(),
      size: sizeNum,
      account_number: accountNumber.trim(),
      strategies_enabled: strategies,
      active,
    };

    setBusy(true);
    try {
      await onSubmit(input);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <h2>{title}</h2>

      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </label>

      <label>
        Prop firm
        {firmsErr ? (
          <span className="error">Could not load firms: {firmsErr}</span>
        ) : firms === null ? (
          <select disabled>
            <option>Loading firms…</option>
          </select>
        ) : firmOptions.length === 0 ? (
          <span className="muted">
            No active firms yet. <Link to="/firms">Add one</Link>, then come back.
          </span>
        ) : (
          <select
            value={propFirm}
            onChange={(e) => setPropFirm(e.target.value)}
            onFocus={() => void refreshFirms()}
            required
          >
            <option value="">— pick a firm —</option>
            {firmOptions.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name}
                {f.active ? "" : " (inactive)"}
                {f.supports_tradelocker ? "" : " — no TradeLocker"}
              </option>
            ))}
          </select>
        )}
      </label>

      <label>
        Size (USD)
        <input
          type="number"
          min={0}
          step={1}
          value={size}
          onChange={(e) => setSize(e.target.value)}
          required
        />
      </label>

      <label>
        Account number
        <input
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          required
          placeholder="Broker-side identifier"
        />
      </label>

      <label>
        Strategies enabled
        <textarea
          rows={3}
          value={strategiesText}
          onChange={(e) => setStrategiesText(e.target.value)}
          placeholder="CCC-NAS100-ORB-v1.0, CCC-NAS100-IBS-v1.0"
        />
        <small>Comma-separated. Format: <code>CCC-&lt;INSTRUMENT&gt;-&lt;STRATEGY&gt;-v&lt;MAJOR&gt;.&lt;MINOR&gt;</code></small>
      </label>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />
        Active
      </label>

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
